import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { readNorthstarJson } from '../../../api/northstarRequest';
import { parseProfileJson } from './profile';

const endpoint = `${(process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '')}/quality/station-runs/northstar-pilot/configuration-packages`;
const panel = { padding: '1.2rem', marginBottom: '1rem', border: '1px solid var(--ni-border-soft)', borderRadius: 10 };
const muted = { color: 'var(--ni-text-muted)', lineHeight: 1.5 };

export function validateSourceImport(input) {
  if (!input || input.schemaVersion !== 'northstar-source-import/1' || input.testOnly !== true
    || !['gpv', 'nin'].includes(input.sourceScope) || !/^[a-f0-9]{64}$/.test(input.sourceManifestHash || '')
    || !Array.isArray(input.packages) || !input.packages.length || input.packages.length > 100) {
    throw new Error('Choose a prepared Northstar source import containing 1–100 Test drafts.');
  }
  const identities = new Set();
  for (const item of input.packages) {
    if (!item || typeof item.documentNumber !== 'string' || typeof item.revision !== 'string'
      || item.profileSnapshot !== null || !Array.isArray(item.readinessBlockers) || !item.readinessBlockers.length
      || !Array.isArray(item.productNumbers) || !item.productNumbers.length
      || 'status' in item || 'productionApproved' in item) throw new Error('Source capture imports must contain incomplete Draft recipes only.');
    const key = JSON.stringify([item.documentNumber, item.revision]);
    if (identities.has(key)) throw new Error('The import repeats a package revision.');
    identities.add(key);
  }
  return input;
}

export default function NorthstarPackages() {
  const { getToken, userId, sessionId } = useAuth();
  const scope = JSON.stringify([userId, sessionId]);
  const currentScope = useRef(scope); currentScope.current = scope;
  const ownerRef = useRef(null), fileRequest = useRef(0);
  const [boundScope, setBoundScope] = useState(scope), [batch, setBatch] = useState(null);
  const [packages, setPackages] = useState([]), [nextBefore, setNextBefore] = useState(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [progress, setProgress] = useState('');
  const [receipts, setReceipts] = useState([]);
  const current = useCallback(owner => owner && !owner.disposed && owner.scope === currentScope.current && ownerRef.current === owner, []);

  const request = useCallback(async (owner, suffix = '', options = {}) => {
    if (!current(owner)) throw new Error('The signed-in session changed.');
    const token = await getToken();
    if (!current(owner) || !token) throw new Error('Sign in with an NI staff account to prepare packages.');
    const controller = new AbortController(); owner.requests.add(controller);
    try {
      return await readNorthstarJson(endpoint + suffix, { ...options, maxBytes: 512 * 1024, signal: controller.signal,
        headers: { Authorization: `Bearer ${token}`, ...(options.body ? { 'Content-Type': 'application/json' } : {}) } });
    } finally { owner.requests.delete(controller); }
  }, [current, getToken]);

  const load = useCallback(async (owner, before = null) => {
    const response = await request(owner, before ? `?before=${encodeURIComponent(before)}` : '');
    if (!current(owner)) return;
    setPackages(previous => before ? [...previous, ...response.configurationPackages] : response.configurationPackages);
    setNextBefore(response.nextBefore || null);
  }, [current, request]);

  useEffect(() => {
    const owner = { scope, requests: new Set(), disposed: false }; ownerRef.current = owner;
    fileRequest.current += 1; setBoundScope(scope); setBatch(null); setPackages([]); setReceipts([]);
    setNextBefore(null); setError(''); setProgress(''); setBusy(true);
    if (userId) load(owner).catch(failure => { if (current(owner)) setError(failure.message); })
      .finally(() => { if (current(owner)) setBusy(false); });
    else setBusy(false);
    return () => { owner.disposed = true; owner.requests.forEach(controller => controller.abort()); };
  }, [scope, userId, current, load]);

  async function selectFile(file) {
    const owner = ownerRef.current, generation = ++fileRequest.current;
    setBatch(null); setReceipts([]); setError(''); setProgress('');
    try {
      if (!file || !file.size || file.size > 512 * 1024) throw new Error('Choose a prepared source import of at most 512 KiB.');
      const parsed = validateSourceImport(parseProfileJson(await file.text()));
      if (current(owner) && generation === fileRequest.current) setBatch(parsed);
    } catch (failure) { if (current(owner) && generation === fileRequest.current) setError(failure.message); }
  }

  async function importDrafts() {
    const owner = ownerRef.current;
    if (!batch || busy || !current(owner)) return;
    setBusy(true); setError(''); setReceipts([]);
    let confirmed = 0;
    try {
      for (const item of batch.packages) {
        if (!current(owner)) return;
        setProgress(`Preparing ${confirmed + 1} of ${batch.packages.length}: ${item.documentNumber}`);
        const response = await request(owner, '', { method: 'POST', body: JSON.stringify(item) });
        if (!current(owner)) return;
        const receipt = response.configurationPackage;
        if (!receipt?.id || receipt.documentNumber !== item.documentNumber || receipt.revision !== item.revision
          || receipt.status !== 'Draft') throw new Error('QMS did not confirm the expected Draft package.');
        confirmed += 1;
        setReceipts(previous => [...previous, { ...receipt, replayed: response.replayed }]);
      }
      if (current(owner)) { setProgress(`${confirmed} Test drafts confirmed in QMS. Hardware remains blocked until each recipe is qualified.`); await load(owner); }
    } catch (failure) {
      if (current(owner)) {
        const message = `${confirmed} drafts confirmed. ${failure.message} Keep this exact import file and retry to reconcile an uncertain save; do not create a different revision.`;
        setProgress('Import stopped.'); setError(message);
        try { await load(owner); }
        catch { if (current(owner)) setError(`${message} The retained-package list could not be refreshed.`); }
      }
    } finally { if (current(owner)) setBusy(false); }
  }

  async function loadMore() {
    const owner = ownerRef.current; setBusy(true); setError('');
    try { await load(owner, nextBefore); } catch (failure) { if (current(owner)) setError(failure.message); }
    finally { if (current(owner)) setBusy(false); }
  }

  return <div><h1>Northstar configuration preparation</h1>
    <p style={muted}>Engineering prepares source-backed Test packages here. Operators receive the matching package automatically from their monitor's service tag.</p>
    {boundScope !== scope ? <p role="status">Changing signed-in session…</p> : <>
      <section style={panel}><h2>Import recovered source packages</h2>
        <p>Imports preserve source revisions, hashes and exact product assignments as Drafts. They do not enable hardware calibration or approve production use.</p>
        <label>Prepared source import <input aria-label="Prepared source import" type="file" accept=".json,application/json" disabled={busy} onChange={event => selectFile(event.target.files?.[0])} /></label>
        {batch && <div><p><strong>{batch.packages.length} Draft packages</strong> · {batch.sourceScope.toUpperCase()} source · Test only</p>
          <ul>{batch.packages.map(item => <li key={`${item.documentNumber}@${item.revision}`}>{item.documentNumber} · {item.revision} · {item.productNumbers.length} exact products · {item.readinessBlockers.length} unresolved requirements</li>)}</ul>
          <button className="ni-button ni-button--primary" type="button" disabled={busy} onClick={importDrafts}>Import Test drafts</button></div>}
        {progress && <p role="status">{progress}</p>}{error && <p role="alert">{error}</p>}
        {!!receipts.length && <details open><summary>Confirmed receipts</summary><ul>{receipts.map(item => <li key={item.id}>{item.documentNumber} · {item.revision} · {item.replayed ? 'Existing identical draft' : 'Saved draft'} · {item.id}</li>)}</ul></details>}
      </section>
      <section style={panel}><h2>Retained packages</h2>
        {!packages.length && <p>{busy ? 'Loading packages…' : 'No prepared packages are recorded.'}</p>}
        {packages.map(item => <article key={item.id} style={{ margin: '1rem 0' }}><strong>{item.documentNumber}</strong> · {item.revision} · {item.status}
          <p style={muted}>{item.productNumbers?.length || 0} exact product assignments · {item.readinessBlockers?.length || 0} recorded preparation requirements</p>
          {item.storageState === 'failed' && <p>Files need verification. Retry the original source import.</p>}
          {item.storageState === 'preparing' && <p>Files are being stored and verified.</p>}
          {item.storageState === 'ready' && <p>Source files stored and verified.</p>}</article>)}
        {nextBefore && <button className="ni-button" type="button" disabled={busy} onClick={loadMore}>Load more packages</button>}
      </section>
    </>}
    <p><Link to="/quality/northstar/browser">Open Northstar</Link> · <Link to="/quality/test-tools">Tools and software</Link></p>
  </div>;
}
